import {
  autoUpdate,
  FloatingFocusManager,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { useRouterState } from '@tanstack/react-router';
import type { FocusEvent, SubmitEvent } from 'react';
import { useEffect, useId, useRef, useState } from 'react';

import hamburger from '#/assets/icons/hamburger.svg';
import type { SearchPreviewState } from '#/components/search/searchPagination';
import { SearchPreviewPopover } from '#/components/search/SearchPreviewPopover';
import {
  SEARCH_PREVIEW_MIN_QUERY_LENGTH,
  useSearchPreview,
} from '#/components/search/useSearchPreview';
import { FloatingPortal } from '#/components/ui/FloatingPortal';
import { ArrowRightIcon, Icon, SearchIcon } from '#/components/ui/icons';
import { Spinner } from '#/components/ui/Spinner';
import { pushEvent } from '#/lib/gtm';
import { cn } from '#/utils/cn';

const SEARCH_PREVIEW_TRANSITION_DURATION_MS = 150;

function getInitialTransitionTransform(side: string) {
  switch (side) {
    case 'top':
      return 'translateY(8px)';
    case 'bottom':
      return 'translateY(-8px)';
    case 'left':
      return 'translateX(8px)';
    default:
      return 'translateX(-8px)';
  }
}

export function MenuIcon({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('flex h-16 w-16 flex-col justify-center', className)}
    >
      <img src={hamburger} alt="" className="h-16 w-auto" />
    </span>
  );
}

export function DisclosureIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className={cn(
        'text-fg-header h-12 w-12 shrink-0 transition-transform',
        expanded ? 'rotate-180' : undefined,
      )}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 4.5L6 8L10 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function OutlineChevron({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 12 12"
      className={cn(
        'text-fg-header h-12 w-12 shrink-0 transition-transform',
        expanded ? 'rotate-180' : undefined,
      )}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.5 4L6 7.5L9.5 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface SearchAffordanceProps {
  searchLabel: string;
  searchPrompt: string;
  /**
   * Analytics grouping bucket for the submit button (`data-track-context`).
   * Shared primitive, so the two nav call sites pass different values
   * (`search_bar` for category nav, `homepage_search` for the homepage hero).
   */
  trackContext?: string;
  searchPlaceholder?: string;
  query?: string;
  searchState?: SearchPreviewState;
  enablePreview?: boolean;
  formClassName?: string;
  condensed?: boolean;
  autoFocus?: boolean;
  showLeadingLabel?: boolean;
  showActions?: boolean;
  isPending?: boolean;
  onSubmitQuery?: (query: string) => void;
  onClick?: () => void;
  onPointerDownWithin?: () => void;
  onFocusWithin?: (event: FocusEvent<HTMLFormElement>) => void;
  onBlurWithin?: (event: FocusEvent<HTMLFormElement>, value: string) => void;
}

export function SearchAffordance({
  searchLabel,
  searchPrompt,
  searchPlaceholder,
  query = '',
  searchState,
  enablePreview = false,
  trackContext,
  formClassName,
  condensed = false,
  autoFocus = false,
  showLeadingLabel = true,
  showActions = true,
  isPending = false,
  onSubmitQuery,
  onClick,
  onPointerDownWithin,
  onFocusWithin,
  onBlurWithin,
}: SearchAffordanceProps) {
  if (!enablePreview) {
    return (
      <SearchAffordanceWithoutPreview
        searchLabel={searchLabel}
        searchPrompt={searchPrompt}
        searchPlaceholder={searchPlaceholder}
        query={query}
        trackContext={trackContext}
        formClassName={formClassName}
        condensed={condensed}
        autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
        showLeadingLabel={showLeadingLabel}
        showActions={showActions}
        isPending={isPending}
        onSubmitQuery={onSubmitQuery}
        onClick={onClick}
        onPointerDownWithin={onPointerDownWithin}
        onFocusWithin={onFocusWithin}
        onBlurWithin={onBlurWithin}
      />
    );
  }

  return (
    <SearchAffordanceWithPreview
      searchLabel={searchLabel}
      searchPrompt={searchPrompt}
      searchPlaceholder={searchPlaceholder}
      query={query}
      searchState={searchState}
      trackContext={trackContext}
      formClassName={formClassName}
      condensed={condensed}
      autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
      showLeadingLabel={showLeadingLabel}
      showActions={showActions}
      isPending={isPending}
      onSubmitQuery={onSubmitQuery}
      onClick={onClick}
      onPointerDownWithin={onPointerDownWithin}
      onFocusWithin={onFocusWithin}
      onBlurWithin={onBlurWithin}
    />
  );
}

function SearchAffordanceWithoutPreview({
  searchLabel,
  searchPrompt,
  searchPlaceholder,
  query = '',
  trackContext,
  formClassName,
  condensed = false,
  autoFocus = false,
  showLeadingLabel = true,
  showActions = true,
  isPending = false,
  onSubmitQuery,
  onClick,
  onPointerDownWithin,
  onFocusWithin,
  onBlurWithin,
}: Omit<SearchAffordanceProps, 'investmentType' | 'enablePreview'>) {
  const inputId = useId();
  const [value, setValue] = useState(query);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasValue = value.trim().length > 0;

  useEffect(() => {
    setValue(query);
  }, [query]);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    searchInputRef.current?.focus();
  }, [autoFocus]);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedQuery = value.trim();

    if (!trimmedQuery) {
      return;
    }

    // Code-emitted on a real submit so the raw term (approved PII) only ships
    // when the user actually searches — never as a stray empty-term click.
    // This variant has no router hook (it can render outside a RouterProvider),
    // so the path is read from the live location at submit time.
    pushEvent({
      event: 'mdc_search_submit',
      search_term: trimmedQuery,
      context: trackContext,
      page_path: window.location.pathname,
    });

    setValue(trimmedQuery);
    onSubmitQuery?.(trimmedQuery);
  };

  function handleClearQuery() {
    setValue('');
    searchInputRef.current?.focus();
  }

  return (
    <form
      aria-label={searchLabel}
      onSubmit={handleSubmit}
      onPointerDown={onPointerDownWithin}
      onFocus={onFocusWithin}
      onBlur={(event) => {
        if (event.currentTarget.contains(event.relatedTarget)) {
          return;
        }

        onBlurWithin?.(event, value);
      }}
      className={cn(
        'border-blue-30 text-fg-header bg-gray-10 flex items-center rounded-full border shadow-[0_1px_0_rgba(1,63,101,0.04)] transition-[background-color,box-shadow,transform,min-height] duration-200 ease-out focus-within:ring-2 focus-within:ring-blue-50 focus-within:ring-offset-2 focus-within:ring-offset-transparent motion-reduce:transition-none',
        condensed
          ? 'min-h-[51px] w-full gap-12 pr-8 pl-20'
          : 'min-h-[59px] w-full gap-12 pr-8 pl-20',
        formClassName,
      )}
    >
      <label htmlFor={inputId} className="sr-only">
        {searchPrompt}
      </label>
      {showLeadingLabel ? (
        <span className="text-static-18 shrink-0 leading-normal font-semibold">
          Find
        </span>
      ) : (
        <SearchIcon size={16} className="text-fg-header shrink-0" />
      )}
      <input
        ref={searchInputRef}
        id={inputId}
        name="query"
        type="search"
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder={searchPlaceholder ?? searchPrompt}
        className={cn(
          'text-static-18 min-w-0 flex-1 bg-transparent py-12 leading-normal outline-none placeholder:text-slate-50 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
          hasValue && 'pr-48',
        )}
      />

      {showActions ? (
        <div className="relative flex size-40 shrink-0 items-center justify-end">
          <button
            type="submit"
            aria-label={searchLabel}
            onClick={onClick}
            className={cn(
              'text-button-primary-bg relative z-10 flex size-40 shrink-0 items-center justify-center rounded-full transition-transform duration-200 ease-out motion-reduce:transition-none',
              hasValue ? '-translate-x-48' : 'translate-x-0',
            )}
            disabled={isPending}
          >
            {isPending ? (
              <Spinner size={20} />
            ) : (
              <ArrowRightIcon size={16} className="shrink-0" />
            )}
          </button>

          <button
            type="button"
            aria-label="Clear search"
            aria-hidden={!hasValue}
            tabIndex={hasValue ? undefined : -1}
            onClick={handleClearQuery}
            data-track="search_clear"
            data-track-context={trackContext}
            className={cn(
              'text-button-primary-bg absolute top-1/2 right-0 z-20 flex size-40 shrink-0 -translate-y-1/2 items-center justify-center rounded-full transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
              hasValue
                ? 'pointer-events-auto translate-x-0 scale-100 opacity-100 delay-100'
                : 'pointer-events-none translate-x-8 scale-90 opacity-0 delay-0',
            )}
            disabled={!hasValue}
          >
            <Icon id="close" size={16} className="shrink-0" />
          </button>
        </div>
      ) : null}
    </form>
  );
}

function SearchAffordanceWithPreview({
  searchLabel,
  searchPrompt,
  searchPlaceholder,
  query = '',
  searchState,
  trackContext,
  formClassName,
  condensed = false,
  autoFocus = false,
  showLeadingLabel = true,
  showActions = true,
  isPending = false,
  onSubmitQuery,
  onClick,
  onPointerDownWithin,
  onFocusWithin,
  onBlurWithin,
}: Omit<SearchAffordanceProps, 'enablePreview'>) {
  const inputId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const previewPointerInteractionRef = useRef(false);
  const previewPointerInteractionTimeoutRef = useRef<number | null>(null);
  // Live SPA route for `page_path` on code-emitted search events. Mirrors the
  // delegated listener's `router.state.location.pathname` source (app.tsx) so
  // these events carry the same path without hardcoding '/search' (the search
  // affordance also renders on the homepage hero and category nav).
  const pagePath = useRouterState({
    select: (state) => state.location.pathname,
  });
  // De-dupe guard for `mdc_search_results_shown`: holds the last emitted
  // `${normalizedQuery}:${previewItemCount}` so a re-render with the same
  // settled state does not re-fire (mirrors the scroll-depth de-dupe ref).
  const lastResultsShownKeyRef = useRef<string | null>(null);
  const searchStateKey = JSON.stringify(searchState ?? {});
  const [value, setValue] = useState(query);
  const hasValue = value.trim().length > 0;
  const searchInputRef = useRef<HTMLInputElement>(null);
  const previewItemRefs = useRef<Array<HTMLElement | null>>([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState<number | null>(
    null,
  );
  const preview = useSearchPreview(value, searchState, isPreviewOpen);
  const shouldShowPreview = isPreviewOpen && preview.shouldRenderPreview;
  const previewItemCount =
    (preview.topResult?.ticker ? 1 : 0) +
    (preview.hasPreviewResults ? 1 : 0) +
    preview.groups.reduce(
      (count, group) =>
        count + group.results.filter((result) => result.ticker != null).length,
      0,
    );
  const { context, refs, floatingStyles } = useFloating({
    open: shouldShowPreview,
    onOpenChange: setIsPreviewOpen,
    placement: 'bottom-start',
    middleware: [
      offset(8),
      shift({ padding: 16 }),
      size({
        padding: 16,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context, {
    enabled: shouldShowPreview,
    referencePress: false,
  });
  const role = useRole(context, { role: 'combobox' });
  const listNavigation = useListNavigation(context, {
    enabled: shouldShowPreview && previewItemCount > 0,
    listRef: previewItemRefs,
    activeIndex: activePreviewIndex,
    onNavigate: setActivePreviewIndex,
    virtual: true,
    loop: true,
    allowEscape: true,
  });
  const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions(
    [dismiss, role, listNavigation],
  );
  const { isMounted: isPreviewMounted, styles: previewTransitionStyles } =
    useTransitionStyles(context, {
      duration: SEARCH_PREVIEW_TRANSITION_DURATION_MS,
      initial: ({ side }) => ({
        opacity: 0,
        transform: getInitialTransitionTransform(side),
      }),
      common: {
        willChange: 'transform, opacity',
      },
    });

  useEffect(() => {
    setValue(query);
  }, [query]);

  useEffect(() => {
    refs.setReference(formRef.current);
    refs.setPositionReference(formRef.current);
  }, [refs]);

  useEffect(() => {
    return () => {
      if (previewPointerInteractionTimeoutRef.current !== null) {
        window.clearTimeout(previewPointerInteractionTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    searchInputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    setActivePreviewIndex(null);
  }, [preview.normalizedQuery, searchStateKey]);

  useEffect(() => {
    previewItemRefs.current = previewItemRefs.current.slice(
      0,
      previewItemCount,
    );

    if (activePreviewIndex != null && activePreviewIndex >= previewItemCount) {
      setActivePreviewIndex(null);
    }
  }, [activePreviewIndex, previewItemCount]);

  // Emit `mdc_search_results_shown` once per settled (query, count) while the
  // preview is shown. Suppressed until the preview stops refreshing so the
  // count reflects what the user actually sees. The de-dupe ref prevents
  // re-firing on unrelated re-renders; a change to either the query or the
  // count re-fires. PII guard: send `query_length`, never the query text.
  useEffect(() => {
    if (!shouldShowPreview || preview.isRefreshingPreview) {
      return;
    }

    const key = `${preview.normalizedQuery}:${previewItemCount}`;

    if (key === lastResultsShownKeyRef.current) {
      return;
    }

    lastResultsShownKeyRef.current = key;

    pushEvent({
      event: 'mdc_search_results_shown',
      result_count: previewItemCount,
      query_length: preview.normalizedQuery.length,
      page_path: pagePath,
    });
  }, [
    shouldShowPreview,
    previewItemCount,
    preview.normalizedQuery,
    preview.isRefreshingPreview,
    pagePath,
  ]);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedQuery = value.trim();

    if (!trimmedQuery) {
      return;
    }

    // Code-emitted on a real submit so the raw term (approved PII) only ships
    // when the user actually searches — never as a stray empty-term click.
    pushEvent({
      event: 'mdc_search_submit',
      search_term: trimmedQuery,
      context: trackContext,
      page_path: pagePath,
    });

    setValue(trimmedQuery);
    setIsPreviewOpen(false);
    onSubmitQuery?.(trimmedQuery);
  };

  function handleClearQuery() {
    setValue('');
    setIsPreviewOpen(false);
    setActivePreviewIndex(null);
    searchInputRef.current?.focus();
  }

  function handlePreviewPointerDownCapture() {
    previewPointerInteractionRef.current = true;

    if (previewPointerInteractionTimeoutRef.current !== null) {
      window.clearTimeout(previewPointerInteractionTimeoutRef.current);
    }

    previewPointerInteractionTimeoutRef.current = window.setTimeout(() => {
      previewPointerInteractionRef.current = false;
      previewPointerInteractionTimeoutRef.current = null;
    }, 0);
  }

  return (
    <div className="relative">
      <form
        ref={formRef}
        aria-label={searchLabel}
        onSubmit={handleSubmit}
        onPointerDown={onPointerDownWithin}
        onFocus={onFocusWithin}
        onBlur={(event) => {
          const nextFocusedElement = event.relatedTarget as Node | null;

          if (
            event.currentTarget.contains(nextFocusedElement) ||
            refs.floating.current?.contains(nextFocusedElement) ||
            previewPointerInteractionRef.current
          ) {
            return;
          }

          setIsPreviewOpen(false);
          onBlurWithin?.(event, value);
        }}
        className={cn(
          'border-blue-30 text-fg-header bg-gray-10 flex items-center rounded-full border shadow-[0_1px_0_rgba(1,63,101,0.04)] transition-[background-color,box-shadow,transform,min-height] duration-200 ease-out focus-within:ring-2 focus-within:ring-blue-50 focus-within:ring-offset-2 focus-within:ring-offset-transparent motion-reduce:transition-none',
          condensed
            ? 'min-h-[51px] w-full gap-12 pr-8 pl-20'
            : 'min-h-[59px] w-full gap-12 pr-8 pl-20',
          formClassName,
        )}
      >
        <label htmlFor={inputId} className="sr-only">
          {searchPrompt}
        </label>
        {showLeadingLabel ? (
          <span className="text-static-18 shrink-0 leading-normal font-semibold">
            Find
          </span>
        ) : (
          <SearchIcon size={16} className="text-fg-header shrink-0" />
        )}
        <input
          ref={searchInputRef}
          id={inputId}
          name="query"
          type="search"
          value={value}
          placeholder={searchPlaceholder ?? searchPrompt}
          {...getReferenceProps({
            onFocus() {
              if (value.trim().length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH) {
                setIsPreviewOpen(true);
              }
            },
            onChange(event) {
              const nextValue = (event.currentTarget as HTMLInputElement).value;

              setValue(nextValue);
              setIsPreviewOpen(
                nextValue.trim().length >= SEARCH_PREVIEW_MIN_QUERY_LENGTH,
              );
            },
            onKeyDown(event) {
              if (
                event.key === 'Enter' &&
                activePreviewIndex != null &&
                previewItemRefs.current[activePreviewIndex]
              ) {
                event.preventDefault();
                previewItemRefs.current[activePreviewIndex].click();
              }
            },
          })}
          className={cn(
            'text-static-18 min-w-0 flex-1 bg-transparent py-12 leading-normal outline-none placeholder:text-slate-50 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none',
            hasValue && 'pr-48',
          )}
        />

        {showActions ? (
          <div className="relative flex size-40 shrink-0 items-center justify-end">
            <button
              type="submit"
              aria-label={searchLabel}
              onClick={onClick}
              className={cn(
                'text-button-primary-bg relative z-10 flex size-40 shrink-0 items-center justify-center rounded-full transition-transform duration-200 ease-out motion-reduce:transition-none',
                hasValue ? '-translate-x-48' : 'translate-x-0',
              )}
              disabled={isPending}
            >
              {isPending ? (
                <Spinner size={20} />
              ) : (
                <ArrowRightIcon size={16} className="shrink-0" />
              )}
            </button>

            <button
              type="button"
              aria-label="Clear search"
              aria-hidden={!hasValue}
              tabIndex={hasValue ? undefined : -1}
              onClick={handleClearQuery}
              data-track="search_clear"
              data-track-context={trackContext}
              className={cn(
                'text-button-primary-bg absolute top-1/2 right-0 z-20 flex size-40 shrink-0 -translate-y-1/2 items-center justify-center rounded-full transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                hasValue
                  ? 'pointer-events-auto translate-x-0 scale-100 opacity-100 delay-100'
                  : 'pointer-events-none translate-x-8 scale-90 opacity-0 delay-0',
              )}
              disabled={isPending || !hasValue}
            >
              <Icon id="close" size={16} className="shrink-0" />
            </button>
          </div>
        ) : null}
      </form>

      {isPreviewMounted ? (
        <FloatingPortal>
          <FloatingFocusManager
            context={context}
            modal={false}
            initialFocus={-1}
            returnFocus={false}
            guards={false}
          >
            <SearchPreviewPopover
              setFloating={refs.setFloating}
              floatingStyles={floatingStyles}
              transitionStyles={previewTransitionStyles}
              getFloatingProps={getFloatingProps}
              getItemProps={getItemProps}
              open={shouldShowPreview}
              query={preview.normalizedQuery}
              previewItemCount={previewItemCount}
              pagePath={pagePath}
              searchState={searchState}
              topResult={preview.topResult}
              groups={preview.groups}
              hasPreviewResults={preview.hasPreviewResults}
              isRefreshing={preview.isRefreshingPreview}
              showLoadingOnlyState={preview.showLoadingOnlyState}
              showEmptyState={preview.showEmptyState}
              activeIndex={activePreviewIndex}
              itemRefs={previewItemRefs}
              onPointerDownCapture={handlePreviewPointerDownCapture}
              onClose={() => setIsPreviewOpen(false)}
            />
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </div>
  );
}
